import { ReceiptData, buildReceiptHtml, printReceiptHtml, openReceiptWindow } from "./receiptTemplate";
import { buildReceiptEscPos } from "./escposReceipt";
import { isWebUSBSupported, getPairedPrinter, sendRawToUsbPrinter } from "./webUsbPrinter";
import { sendRawToBridge } from "./printBridgeClient";

export interface PrintReceiptResult {
  ok: boolean;
  method: "bridge" | "usb" | "browser" | "none";
  message?: string;
}

const acquireUsbPrinter = async (): Promise<any | null> => {
  if (!isWebUSBSupported()) return null;
  try {
    // Silent check only — never prompt user with modal requestDevice() during standard receipt printing
    return await getPairedPrinter();
  } catch {
    return null;
  }
};

// A guard scoped to a React hook instance (usePrintReceipt) only stops two
// clicks *within that same mounted component* from racing — CheckoutPage
// and OrderPage each mount their own instance, so a print started on one
// page, still awaiting USB device acquisition, doesn't block a second one
// started after navigating to the other page (or from a second browser
// tab entirely). Both would then race sendRawToUsbPrinter()'s
// claimInterface() call on the same physical USB connection. The lock
// below is module-scoped (shared by every caller in this tab) and, where
// available, uses the Web Locks API for genuine cross-tab exclusion too —
// Chrome/Edge only, but this feature already requires that browser for
// WebUSB itself, so it's not a new constraint.
const PRINT_LOCK_NAME = "pos-receipt-print";
let inFlightInThisTab = false;

const withPrintLock = async <T,>(fn: () => Promise<T>): Promise<T | { locked: true }> => {
  const locksApi = typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
  if (locksApi?.request) {
    return locksApi.request(PRINT_LOCK_NAME, { ifAvailable: true }, (lock: any) => {
      if (!lock) return { locked: true as const };
      return fn();
    });
  }
  // No Web Locks support — same-tab-only fallback. Doesn't cover the
  // cross-tab case, but is still a strict improvement over no shared guard
  // at all (which was the previous state: each hook instance's own ref).
  if (inFlightInThisTab) return { locked: true as const };
  inFlightInThisTab = true;
  try {
    return await fn();
  } finally {
    inFlightInThisTab = false;
  }
};

// Single entry point both CheckoutPage and OrderPage's "Generate Bill" call
// instead of building HTML and calling printReceiptHtml directly. Tries:
//   1. Local Print Bridge (127.0.0.1:9100) — instant, silent, zero popup dialogs
//   2. WebUSB direct pairing (if supported and bridge not available)
//   3. Browser print preview dialog (safe universal fallback)
export const printReceipt = async (
  getData: () => ReceiptData | Promise<ReceiptData>
): Promise<PrintReceiptResult> => {
  const result = await withPrintLock(() => printReceiptLocked(getData));
  if ("locked" in result) {
    return { ok: false, method: "none", message: "Another print is already in progress — please wait a moment and try again" };
  }
  return result;
};

const printReceiptLocked = async (
  getData: () => ReceiptData | Promise<ReceiptData>
): Promise<PrintReceiptResult> => {
  const data = await getData();
  const escposBytes = buildReceiptEscPos(data);

  // 1. Attempt Local Print Bridge (spools raw bytes directly via winspool.drv)
  // Instant, completely silent, zero popup windows, zero device picker prompts
  try {
    const bridgeResult = await sendRawToBridge(escposBytes);
    if (bridgeResult.ok) {
      return { ok: true, method: "bridge" };
    }
  } catch {
    // Local bridge is offline or unavailable — continue to USB/browser fallback
  }

  // 2. Attempt WebUSB (silent check for previously paired printer)
  const device = await acquireUsbPrinter();
  if (device) {
    try {
      await sendRawToUsbPrinter(device, escposBytes);
      return { ok: true, method: "usb" };
    } catch (err: any) {
      console.warn("USB receipt print failed, falling back to browser print dialog:", err);
    }
  }

  // 3. Fallback to browser print dialog (only opened when bridge & USB unavailable)
  const popupWindow = openReceiptWindow();
  const opened = printReceiptHtml(buildReceiptHtml(data), popupWindow);
  return {
    ok: opened,
    method: opened ? "browser" : "none",
    // Distinguishes a genuinely blocked popup (popupWindow was null from
    // the start — openReceiptWindow() itself failed) from one that DID
    // open but was then closed by the user before we got here (the
    // win.closed check inside printReceiptHtml) — "allow popups" is the
    // wrong instruction for someone who already has popups allowed and
    // just needs to leave the window open and try again.
    message: opened
      ? undefined
      : popupWindow === null
        ? "Allow popups to print the receipt"
        : "The print window was closed before the receipt was ready — click Print again and leave it open",
  };
};
